import { isMobileAtom } from '@/jotai/atoms/userAtom';
import {
  Badge,
  Button,
  Col,
  Form,
  Image,
  Input,
  message,
  Row,
  Space,
  Table,
  TableColumnType,
  Tabs,
  Tag,
} from 'antd';
import { useAtomValue } from 'jotai';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ComfirmDialog from './comfirmDialog';
import {
  deleteExhibition,
  Exhibition,
  ExhibitionFeeStatus,
  ExhibitionListParams,
  ExhibitionStatus,
  getCommentCount,
  getExhibitionList,
  offlineExhibition,
  onlineExhibition,
} from '@/api/exhibition';
import { FORMAT_TIME } from '@/types/common';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { PageLink } from '@/config/pageConfig';
import { useRequest } from 'ahooks';

const ExhibitionPage = () => {
  const isMobile = useAtomValue(isMobileAtom);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [messageApi, contextHolder] = message.useMessage();

  const [form] = Form.useForm<{ title: string }>();
  const [tab, setTab] = useState(
    searchParams.get('tab') || `${ExhibitionStatus.Approved}`
  );
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [comfirmDialogOpen, setComfirmDialogOpen] = useState(false);
  const [comfirmDialogTitle, setComfirmDialogTitle] = useState('');
  const [confirmBtnText, setConfirmBtnText] = useState('');
  const [isDanger, setIsDanger] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = isMobile ? 10 : 20;
  const [currentActionIds, setCurrentActionIds] = useState<number[]>([]);
  const [currentActionStatus, setCurrentActionStatus] = useState<
    ExhibitionStatus | undefined
  >();
  const [actionType, setActionType] = useState<
    'changeStatus' | 'delete' | undefined
  >();

  const handleTabChange = (key: string) => {
    setTab(key);
    const params = new URLSearchParams(searchParams);
    params.set('tab', key);
    setSearchParams(params);
  };

  const {
    data: exhibitionList,
    isLoading,
    refetch: refetchExhibitionList,
  } = useQuery({
    queryKey: ['exhibitionList', tab, currentPage],
    queryFn: async () => {
      const params: ExhibitionListParams = {
        ...form.getFieldsValue(),
        status: tab !== 'all' ? parseInt(tab) : undefined,
        currentPage,
        pageSize,
      };
      const resp = await getExhibitionList(params);
      if (resp.status !== 200) {
        return null;
      }
      return resp.data;
    },
    refetchOnWindowFocus: false,
  });

  const {
    data: commentCount
  } = useRequest(async () => {
    const res = await getCommentCount()

    if (res.status === 200 && res.data.success) {
      return res.data.data;
    }

    return 0;
  })

  const handleSearch = () => {
    setCurrentPage(1);
    void refetchExhibitionList();
  };

  const handleReset = () => {
    form.resetFields();
    setCurrentPage(1);
    void refetchExhibitionList();
  };

  const columns: TableColumnType<Exhibition>[] = [
    {
      title: 'sort',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'cover',
      dataIndex: 'cover',
      key: 'cover',
      render: (_, record) =>
        !record.exhibitionImageList ||
          record.exhibitionImageList.length === 0 ? (
          <span>暂无封面</span>
        ) : (
          <Image
            src={record.exhibitionImageList[0].imageUrl}
            preview={false}
            width={100}
          />
        ),
    },
    {
      title: 'title',
      dataIndex: 'title',
      key: 'title',
      render: (value, record) => {
        if (record.status === ExhibitionStatus.Draft) {
          return <Space>{value} <Tag color="orange">草稿</Tag></Space>
        }

        return <Space>{value}</Space>
      }
    },
    {
      title: 'createUser',
      dataIndex: 'createUser',
      key: 'createUser',
    },
    {
      title: 'updateUser',
      dataIndex: 'updateUser',
      key: 'updateUser',
    },
    {
      title: 'free/ticket',
      key: 'freeTicket',
      render: (_, record) =>
        record.feeStatus === ExhibitionFeeStatus.Free ? 'free' : 'ticket',
    },
    {
      title: 'status',
      dataIndex: 'status',
      key: 'status',
      render: (_, record) => {
        switch (record.status) {
          case ExhibitionStatus.Failed:
            return '图片上传失败';
          case ExhibitionStatus.Pending:
            return '初始状态';
          case ExhibitionStatus.Approved:
            return '上架';
          case ExhibitionStatus.Rejected:
            return '下架';
          case ExhibitionStatus.Draft:
            return '草稿';
          default:
            return '未知状态';
        }
      },
    },
    {
      title: 'update time',
      dataIndex: 'updateTime',
      key: 'updateTime',
      render: (_, record) => dayjs(record.updateTime).format(FORMAT_TIME),
    },
    {
      title: 'action',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" direction="vertical">
          <Button
            type="link"
            size="small"
            onClick={() => navigate(`/exhibition/editor?id=${record.id}`)}
          >
            编辑详情
          </Button>
          {record.status === ExhibitionStatus.Approved && (
            <Button
              type="link"
              size="small"
              onClick={() =>
                handleChangeStatus(record.id, ExhibitionStatus.Rejected)
              }
            >
              下架
            </Button>
          )}
          {
            [ExhibitionStatus.Pending, ExhibitionStatus.Rejected, ExhibitionStatus.Draft].includes(record.status)
            && (
              <Button
                type="link"
                size="small"
                onClick={() =>
                  handleChangeStatus(record.id, ExhibitionStatus.Approved)
                }
              >
                上架
              </Button>
            )}
          <Button
            type="link"
            size="small"
            onClick={() => handleClickDelete(record.id)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  const handleChangeStatus = (id: number, status: ExhibitionStatus) => {
    setActionType('changeStatus');
    setCurrentActionStatus(status);
    setCurrentActionIds([id]);
    setComfirmDialogTitle(
      `确定${status === ExhibitionStatus.Rejected ? '下架' : '上架'}该内容？`
    );
    setConfirmBtnText(status === ExhibitionStatus.Rejected ? '下架' : '上架');
    setIsDanger(status === ExhibitionStatus.Rejected);
    setComfirmDialogOpen(true);
  };

  const handleConfirmChangeStatus = async () => {
    const fetchFn =
      currentActionStatus === ExhibitionStatus.Rejected
        ? offlineExhibition
        : onlineExhibition;

    const resp = await fetchFn(currentActionIds[0]);
    if (resp.status !== 200) {
      messageApi.error(
        `${currentActionStatus === ExhibitionStatus.Rejected ? '下架' : '上架'
        }失败`
      );
      return;
    }
    messageApi.success(
      `${currentActionStatus === ExhibitionStatus.Rejected ? '下架' : '上架'
      }成功`
    );
    setComfirmDialogOpen(false);
    void refetchExhibitionList();
  };

  const handleClickDelete = (id: number) => {
    setCurrentActionIds([id]);
    setActionType('delete');
    setComfirmDialogTitle('确定删除该内容？');
    setConfirmBtnText('删除');
    setIsDanger(true);
    setComfirmDialogOpen(true);
  };

  const handleBatchDelete = () => {
    setCurrentActionIds(selectedKeys.map((key) => parseInt(key)));
    setActionType('delete');
    setComfirmDialogTitle('确定删除全部内容？');
    setConfirmBtnText('删除');
    setIsDanger(true);
    setComfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    const resp = await deleteExhibition(currentActionIds.join(','));
    if (resp.status !== 200) {
      messageApi.error('删除失败');
      return;
    }
    messageApi.success('删除成功');
    setComfirmDialogOpen(false);
    void refetchExhibitionList();
  };

  return (
    <div className="report-container">
      {contextHolder}
      <Form
        form={form}
        labelCol={{
          span: 8,
        }}
        wrapperCol={{
          span: 16,
        }}
        labelAlign="left"
        onFinish={handleSearch}
      >
        <Row gutter={24}>
          <Col span={12} xs={24} sm={12} md={8}>
            <Form.Item name="title" label="Title">
              <Input size="small" />
            </Form.Item>
          </Col>
          <Col span={8} xs={24} sm={16} md={8}>
            <Form.Item>
              <Space size="small">
                <Button type="primary" htmlType="submit" size="small">
                  搜索
                </Button>
                <Button onClick={handleReset} size="small">
                  重置
                </Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <div className="actions-container">
        <Space size="small" style={{ justifyContent: "space-between", width: "100%" }}>
          <Space>
            <Button type="primary" onClick={() => navigate('/exhibition/editor')}>
              创建
            </Button>
            <Button
              type="primary"
              danger
              disabled={selectedKeys.length === 0}
              onClick={handleBatchDelete}
            >
              删除
            </Button>
          </Space>

          <Space style={{ marginLeft: "auto" }}>
            <Badge dot={!!commentCount}>
              <Button type="primary" style={{ marginLeft: "auto" }} onClick={() => navigate(PageLink.ExhibitionComment)}>所有评价{!!commentCount && ` ${commentCount}`}</Button>
            </Badge>
          </Space>
        </Space>
      </div>
      <div style={{ paddingTop: '10px' }}>
        <Tabs
          type="card"
          items={[
            { key: `${ExhibitionStatus.Approved}`, label: '已上架' },
            { key: `${ExhibitionStatus.Rejected}`, label: '下架' },
            { key: 'all', label: '全部' },
          ]}
          activeKey={tab}
          className="tabs-container"
          onChange={handleTabChange}
          size="small"
        />
      </div>
      <Table
        size="small"
        loading={isLoading}
        columns={columns}
        dataSource={exhibitionList?.records}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={{
          size: isMobile ? 'small' : 'default',
          pageSize,
          showSizeChanger: false,
          total: exhibitionList?.total,
          current: currentPage,
          onChange: (page) => setCurrentPage(page),
        }}
        rowSelection={{
          selectedRowKeys: selectedKeys,
          type: 'checkbox',
          fixed: true,
          onChange: (selectedRowKeys) => {
            setSelectedKeys(selectedRowKeys as string[]);
          },
        }}
      />
      <ComfirmDialog
        open={comfirmDialogOpen}
        onOk={() => {
          if (actionType === 'changeStatus') {
            void handleConfirmChangeStatus();
          } else if (actionType === 'delete') {
            void handleConfirmDelete();
          }
        }}
        onCancel={() => setComfirmDialogOpen(false)}
        title={comfirmDialogTitle}
        confirmBtnText={confirmBtnText}
        isDanger={isDanger}
      />
    </div>
  );
};

export default ExhibitionPage;
